import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { CustomerFilters, CustomerSortOrder } from '../components/CustomerSelectionView';

/**
 * Saved filter+sort presets ("views") for the customer list, keyed per user.
 * Persisted to localStorage; future server-backed sync is a drop-in here.
 */
export interface SavedView {
  id: string;
  name: string;
  sortOrder: CustomerSortOrder;
  filters: CustomerFilters;
  selectedView: 'all' | 'customers' | 'prospects' | 'suspects';
  /**
   * Optional manual ordering of customer IDs. Populated when the view is
   * saved while sortOrder === 'custom'. Restoring such a view re-applies the
   * stored ordering through useCustomCustomerOrder.
   */
  customOrder?: string[];
  createdAt: string;
}

const MAX_VIEWS = 10;
const storageKey = (userId: string) => `riskninja:saved-views:${userId}`;

const readViews = (userId: string): SavedView[] => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeViews = (userId: string, views: SavedView[]) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(views));
  } catch {
    // ignore storage errors
  }
};

export const useSavedViews = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [views, setViews] = useState<SavedView[]>([]);

  useEffect(() => {
    if (!userId) {
      setViews([]);
      return;
    }
    setViews(readViews(userId));
  }, [userId]);

  const saveView = useCallback(
    (view: Omit<SavedView, 'id' | 'createdAt'>) => {
      if (!userId) return null;
      const id = `view_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const next: SavedView = { ...view, id, createdAt: new Date().toISOString() };
      setViews((prev) => {
        const updated = [next, ...prev].slice(0, MAX_VIEWS);
        writeViews(userId, updated);
        return updated;
      });
      return next;
    },
    [userId]
  );

  const deleteView = useCallback(
    (id: string) => {
      if (!userId) return;
      setViews((prev) => {
        const updated = prev.filter((v) => v.id !== id);
        writeViews(userId, updated);
        return updated;
      });
    },
    [userId]
  );

  return { views, saveView, deleteView, max: MAX_VIEWS };
};
